import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * 购物车勾选（INSMIA 自建）
 *
 * 场景：用户会先把一堆东西加进车，再决定这次买哪几件。
 *
 * 🔴 为什么不是「纯前端勾选 + 结算时只提交选中项」：
 *   Shopify 的结账是**整车**走的，没有「只结算部分商品」的原生能力；
 *   Storefront API 另开购物车这条路也断了 —— 本店自定义 app 建不了 storefront access token
 *   （`App must be extendable to create a storefront access token`）。
 *
 * ⇒ 做法：**取消勾选 = 真的把这行移出购物车并存进 localStorage，重新勾选 = 加回来**。
 *   购物车里永远只有「选中的商品」，于是小计 / 免邮进度条 / 第二双折扣
 *   **全部由 Shopify 权威计算，零估算误差**，结算也走原生链路。
 *
 * 🔴🔴 性能（2026-09-20 第一版实测「点一下要 1.1 秒才有反应」后重写）：
 *   ① 首版在 document.body 上挂了 subtree MutationObserver —— 页面任何 DOM 变动都会排一次全量重绘，
 *      是卡顿主因。现在只观察购物车容器本身，且每次 morph 后重新挂。
 *   ② 首版点击后**什么都不做**，干等网络往返 + section 重渲染（实测 1109ms）才有视觉变化。
 *      现在改成乐观 UI：点下去立刻置灰/复原，网络在后台跑，失败再回滚。
 *   ③ 连点多个复选框会并发打 /cart/*.js，互相踩。现在串行排队。
 *   ④ paint() 加签名早退，避免 morph 引发的连锁空转。
 */

const STORAGE_KEY = 'insmia:cart:unselected:v1';
const GHOST_CLASS = 'insmia-cart-ghost';

function storageAvailable() {
  try {
    localStorage.setItem('__insmia_probe__', '1');
    localStorage.removeItem('__insmia_probe__');
    return true;
  } catch {
    return false;
  }
}
const ENABLED = storageAvailable();

function readSaved() {
  if (!ENABLED) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => x && x.variantId) : [];
  } catch {
    return [];
  }
}
function writeSaved(list) {
  if (!ENABLED) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* 配额满：降级但不影响购物车 */
  }
}

/* ------------------------------------------------------------------ 串行队列 */
/** 购物车接口不能并发打：连点两个复选框会互相踩，必须排队 */
const queue = [];
let draining = false;
function enqueue(task) {
  queue.push(task);
  drain();
}
async function drain() {
  if (draining) return;
  draining = true;
  try {
    while (queue.length) {
      const task = queue.shift();
      try {
        await task();
      } catch {
        /* 单个任务失败不阻塞后面的 */
      }
    }
  } finally {
    draining = false;
  }
}

/* ------------------------------------------------------------------ 平滑位移（FLIP） */
/* 🔴 勾选 / 取消勾选后「页面跳一下，像加载了一次」（用户 2026-09-27 反馈）。
   逐帧实测不是渲染出错，是**第二双折扣重新分配**：「Second Pair 40% Off」标签从一行挪到另一行
   （手机上标签折两行，行高差 50px），取消的那行变成不带标签的幽灵行 —— 全在服务器回来的那一帧里
   同时发生，前面 1.8 秒只有合计金额在闪，于是像整页重新加载了一次。
   做法：morph 之前记下每行（按变体）、每行数量控件、推荐区 / 结算区的位置；版面落定后，
   位置变了的元素先用 transform 放回原位，再 0.22s 滑到新位置。移动中的元素垫上不透明背景、
   叠在上层 —— 上一行新长出来的标签是被「揭开」的，不会和下一行叠在一起；新出现的标签淡入。
   用 Web Animations（el.animate）而不是改 style：动画结束自动复原，不留任何内联样式。 */
const FLIP_MS = 220;
let flipBefore = null;

function flipCollect() {
  const items = [];
  const seen = new Map();
  document.querySelectorAll('.cart-items__table-row[data-variant-id]').forEach((row) => {
    const box = row.getBoundingClientRect();
    if (!box.height) return;
    // 同一变体可能同时出现在抽屉和购物车页；带属性的多行同变体再按出现次序区分
    const base = (row.closest('.cart-drawer') ? 'd' : 'p') + ':' + row.dataset.variantId;
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    const key = `${base}#${n}`;
    items.push({ key, el: row, top: box.top, chip: !!row.querySelector('.cart-items__discount') });
    const qty = row.querySelector('.cart-items__quantity');
    const qb = qty?.getBoundingClientRect();
    if (qb?.height) items.push({ key: `${key}:qty`, el: qty, top: qb.top, rowTop: box.top });
  });
  document
    .querySelectorAll('.cart-page__summary, .cart-page__recs, .cart-drawer [data-insmia-recs]')
    .forEach((el, index) => {
      const box = el.getBoundingClientRect();
      if (box.height) items.push({ key: `block:${index}`, el, top: box.top });
    });
  return items;
}

function flipCapture() {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    flipBefore = null;
    return;
  }
  const map = new Map();
  for (const item of flipCollect()) map.set(item.key, item);
  flipBefore = { at: Date.now(), map };
}

function flipBackground(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const bg = getComputedStyle(node).backgroundColor;
    if (bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg)) return bg;
  }
  return '#fff';
}

function flipPlay() {
  /* 🔴 先把版面补到最终形态再量：加减数量走的是 dispatchCartUpdate，主题组件是在**事件处理里**
     才 morph 的，而本脚本的 cart:update 监听排在它前面 —— 派发一结束时行还是 Shopify 的顺序、
     幽灵行也被 morph 删了，要等随后的 MutationObserver 微任务才摆好（幽灵行更要等 80ms 防抖）。
     这时候量「新位置」全是错的（实测行从 -27px / 575px 这种位置滑进来）。
     paint() 签名没变会直接早退，对勾选那条路没有额外开销；减弱动效时也要补，所以放在最前面。 */
  paint();
  const before = flipBefore;
  flipBefore = null;
  // 记录超过 3 秒就不用了（请求失败、没走到这里的那次留下的旧位置）
  if (!before || Date.now() - before.at > 3000 || typeof Element.prototype.animate !== 'function') return;

  // 先把所有新位置读完，再统一开动画：读写不交错，只触发一次布局
  const after = flipCollect();
  const rowTopNow = new Map(after.filter((item) => !item.key.endsWith(':qty')).map((item) => [item.key, item.top]));
  const moves = [];
  const chipsIn = [];
  for (const item of after) {
    const old = before.map.get(item.key);
    if (!old) continue;
    let dy;
    if (item.key.endsWith(':qty')) {
      // 数量控件相对「本行」的位移：行自己的位移由行的动画负责，这里只补行内的那一段
      const rowKey = item.key.slice(0, -4);
      const rowNow = rowTopNow.get(rowKey);
      if (rowNow === undefined || old.rowTop === undefined) continue;
      dy = old.top - old.rowTop - (item.top - rowNow);
    } else {
      dy = old.top - item.top;
      if (item.chip && !old.chip) chipsIn.push(item.el.querySelector('.cart-items__discount'));
    }
    if (Math.abs(dy) >= 1) moves.push([item.el, dy]);
  }
  if (!moves.length && !chipsIn.length) return;

  const bg = flipBackground(moves[0]?.[0] || document.body);
  const timing = { duration: FLIP_MS, easing: 'cubic-bezier(.2, .7, .3, 1)' };
  const animations = [];
  for (const [el, dy] of moves) {
    animations.push(
      el.animate(
        [
          { transform: `translateY(${dy}px)`, position: 'relative', zIndex: 1, backgroundColor: bg },
          { transform: 'translateY(0)', position: 'relative', zIndex: 1, backgroundColor: bg },
        ],
        timing
      )
    );
  }
  for (const chip of chipsIn) if (chip) animations.push(chip.animate([{ opacity: 0 }, { opacity: 1 }], timing));

  /* 🔴 先停在起点，等落地这一帧画出来再开始滑。落地这一帧本身可能很慢（勾回来时要重建整行、
     解码图片，实测两帧之间隔了约 100ms）；动画按时间走，直接播放的话第一步就跳掉一半行程。 */
  animations.forEach((animation) => animation.pause());
  requestAnimationFrame(() => requestAnimationFrame(() => animations.forEach((animation) => animation.play())));
}

// 加减数量（insmia-cart-line-tools.js）也会让折扣标签换行，同一套动画
window.insmiaCartFlip = { capture: flipCapture, play: flipPlay };

/* ------------------------------------------------------------------ 网络 */
function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el instanceof HTMLElement ? el.dataset.sectionId : null;
    if (id) ids.add(id);
  });
  return [...ids];
}

/** update.js / change.js 回来的是整车；add.js 只回来加进去的那几行 */
function isFullCart(data) {
  return !!data && Array.isArray(data.items) && typeof data.item_count === 'number';
}

/* 🔴 焦点：morph 要删掉当前有焦点的元素时（刚点过的 Select all、抽屉打开时默认聚焦的 ✕），
   Chrome 会在删除的当场做一次整页样式重算 + 布局来安置焦点（2026-09-27 实测：全不选落地那一刻
   多出 58ms 样式 + 18ms 布局；先 blur 再 morph 就没有这一下）。
   所以 morph 前把焦点收起来，下一帧再还给「同一个」控件 —— 键盘用户的位置也不会丢到页面最上面。 */
function releaseFocus() {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return null;
  if (!active.closest('cart-items-component, .cart-drawer, .cart-page')) return null;
  const inDrawer = !!active.closest('.cart-drawer');
  let find = null;
  if (active.matches('[data-insmia-select-all]')) {
    find = (root) => root.querySelector('[data-insmia-select-all]');
  } else if (active.matches('[data-insmia-select]')) {
    const variantId = String(active.dataset.variantId || '');
    find = (root) => root.querySelector(`[data-insmia-select][data-variant-id="${CSS.escape(variantId)}"]`);
  } else if (active.id) {
    const id = active.id;
    find = () => document.getElementById(id);
  } else if (active.matches('.cart-drawer__close-button')) {
    find = (root) => root.querySelector('.cart-drawer__close-button');
  }
  active.blur();
  if (!find) return null;
  return () => {
    // 这一帧里用户自己把焦点放到别处了就别抢
    if (document.activeElement && document.activeElement !== document.body) return;
    const root = (inDrawer ? document.querySelector('.cart-drawer') : document.querySelector('.cart-page')) || document;
    const target = find(root);
    if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
  };
}

async function postCart(url, body) {
  const ids = sectionIds();
  /* 等服务器这一秒里给合计金额一个「正在更新」的反馈（主题自己改数量时也是这样闪），
     不然只有勾选框变了、金额和 Check out (N) 纹丝不动，像是卡住了（用户 2026-09-26 反馈）。
     🔴 结束时必须自己清掉：全不选时的结算区是快照复原的、不经过 morph，属性不会被自动去掉。 */
  const shimmering = [...document.querySelectorAll('text-component[ref="cartTotal"]')];
  shimmering.forEach((t) => t.setAttribute('shimmer', ''));
  const stopShimmer = () =>
    document.querySelectorAll('text-component[ref="cartTotal"][shimmer]').forEach((t) => t.removeAttribute('shimmer'));

  let res;
  let data;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ids.join(','), sections_url: location.pathname }),
    });
    data = await res.json().catch(() => null);
  } catch (error) {
    stopShimmer();
    throw error;
  }
  if (!res.ok || !data || data.status) {
    stopShimmer();
    throw new Error((data && (data.description || data.message)) || `HTTP ${res.status}`);
  }
  /* 🔴🔴 cart:update 必须带上整车（detail.resource），和主题自己的做法一致。
     不带的话页面上每个商品表单（推荐位 12 张卡、商品页的主表单）都会各自去拉一遍 /cart.js，
     而 Shopify 对同一个购物车的请求是**排队处理**的 ⇒ 每次勾选后后台排着 13 个请求、约 5 秒才排完，
     这期间再点任何勾选 / 全选都得排在它们后面；每个请求回来还各改一次 DOM、各触发一次整页样式重算
     （2026-09-27 实测：全选的请求等了 5.7 秒）。add.js 回来的不是整车 ⇒ 自己补拉一次（一次，不是 13 次）。 */
  let cart = isFullCart(data) ? data : null;
  if (!cart) {
    try {
      const again = await fetch('/cart.js');
      cart = again.ok ? await again.json() : null;
    } catch {
      cart = null;
    }
  }

  const restoreFocus = releaseFocus();
  // 服务器结果落地前的最后一刻记位置（等待的这一两秒里用户可能滚动过，不能在点击时记）
  flipCapture();
  captureSummaryHeights();
  suspendObserver();
  try {
    for (const id of ids) {
      const html = data.sections && data.sections[id];
      if (html) {
        try {
          await morphSection(id, html, { mode: 'full', injectStylesheet: true });
        } catch {
          /* 单个 section 换失败不影响其它 */
        }
      }
    }
  } finally {
    resumeObserver();
    stopShimmer();
  }
  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource: cart || undefined,
        sourceId: 'insmia-cart-select',
        source: 'insmia-cart-select',
        // 主题的购物车图标读 data.itemCount（读不到按 0 处理，会把角标藏掉）
        data: { ...data, source: 'insmia-cart-select', itemCount: cart ? cart.item_count : undefined },
      },
    })
  );
  // 主题的 cart-items 在 cart:update 里又 morph 了一遍：按意图把排队中的勾选摆回来
  applyIntents();
  syncControls();
  // 调用方落地后还要 paint / flipPlay（同步），下一帧再还焦点：那时版面已经排好，不会为了找焦点多算一次样式
  if (restoreFocus) requestAnimationFrame(restoreFocus);
  return data;
}

/**
 * 把这些行（合并行里的每一个行项目也算）移出购物车：一次 update.js，按行 key 清零。
 * 🔴 不再先 GET /cart.js 数行数（多一次往返）。每行都带 data-insmia-keys（合并行是空格分隔的全部 key），
 * BXGY 拆出来的同变体多行 key 各不相同（2026-09-27 实测），清零的就是页面上显示的这些行，不会误删别的。
 * key 过期（别的标签页改过这几行）时 Shopify 按 key 里的变体 id 去找，可能只清掉其中一行 ——
 * 回来的是整车，按它把这些变体剩下的行再清一次，不用再拉 /cart.js。
 */
async function removeLines(rows) {
  const live = rows.map((row) => (row.id && document.getElementById(row.id)) || row);
  const variants = new Set(live.map((row) => String(row.dataset.variantId || '')).filter(Boolean));
  const keys = [
    ...new Set(live.flatMap((row) => String(row.dataset.insmiaKeys || row.dataset.key || '').split(/\s+/)).filter(Boolean)),
  ];
  const url = Theme.routes.cart_update_url || '/cart/update.js';
  let data = await postCart(url, { updates: Object.fromEntries(keys.map((key) => [key, 0])) });
  const left = (data.items || []).filter((line) => variants.has(String(line.variant_id)));
  if (left.length) {
    data = await postCart(url, {
      updates: data.items.map((line) => (variants.has(String(line.variant_id)) ? 0 : line.quantity)),
    });
  }
  return data;
}

/**
 * 取消勾选的商品加回购物车。
 * 🔴 用 update.js 按变体给数量，不用 add.js：add.js 回来的只有加进去的那几行，不是整车（见 postCart 的注释）；
 * update.js 对不在车里的变体就是「加进去」（2026-09-27 实测，含 2 件时同样按第二双折扣拆成两行），回来的是整车。
 * 但它对**已经在车里**的变体是「改成这个数」而不是「再加这么多」—— 顾客取消勾选后又从商品页加购了同款同码，
 * 这时候仍走 add.js（postCart 会补拉一次整车）。
 */
async function addBack(items) {
  const inCart = new Set(
    [...document.querySelectorAll('.cart-items__table-row[data-key][data-variant-id]')].map((row) => String(row.dataset.variantId))
  );
  if (items.some((item) => inCart.has(String(item.id)))) return postCart(Theme.routes.cart_add_url, { items });
  return postCart(Theme.routes.cart_update_url || '/cart/update.js', {
    updates: Object.fromEntries(items.map((item) => [String(item.id), item.quantity])),
  });
}

/** 回来的行里有没有这个变体（update.js 对加不上的变体可能不报错、只是没加 —— 当作加回失败） */
function landed(data, variantId) {
  return (data?.items || []).some((line) => String(line.variant_id) === String(variantId));
}

/* ------------------------------------------------------------------ 幽灵行 */
/** "$1,234.56 USD" → 123456（分）。本店只有 USD，不做多语言解析。 */
function parseMoney(text) {
  const match = String(text || '').match(/[\d.,]+/);
  if (!match) return 0;
  return Math.round(parseFloat(match[0].replace(/,/g, '')) * 100);
}

/** 按样例串的货币装饰重新格式化金额，保住 "$" 和 " USD" 这些前后缀 */
function formatMoney(cents, sample) {
  const match = String(sample || '').match(/^(\D*)([\d.,]+)(.*)$/);
  const prefix = match ? match[1] : '';
  const suffix = match ? match[3] : '';
  const value = (Math.max(0, cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return prefix + value + suffix;
}

function snapshotRow(row) {
  const img = row.querySelector('.cart-items__media-image');
  const titleEl = row.querySelector('.cart-items__title');
  const priceEl = row.querySelector('.cart-items__price');
  const variantEls = [...row.querySelectorAll('.cart-items__variant')];
  // 🔴 记住它在列表里的位置：不记的话幽灵行只能追加到末尾，
  // 取消勾选时那一行会「跳到最下面」，看起来像消失了 —— 说好的是原位灰化。
  const siblings = row.parentElement ? [...row.parentElement.children] : [];
  return {
    variantId: row.dataset.variantId || '',
    index: Math.max(0, siblings.indexOf(row)),
    quantity: Number(row.dataset.quantity || '1') || 1,
    title: (titleEl?.textContent || '').trim(),
    url: titleEl instanceof HTMLAnchorElement ? titleEl.getAttribute('href') || '' : '',
    image: img instanceof HTMLImageElement ? img.currentSrc || img.src : '',
    // 🔴 主题把每个选项渲染成独立的 .cart-items__variant，且除末项外自带尾逗号
    // （"Color: Rich Brown," / "Size: 6"）。原来用 ' / ' 拼，结果是
    // "Color: Rich Brown, / Size: 6"。去掉尾逗号再用 ', ' 拼，与真实行完全一致。
    variantText: variantEls
      .map((el) => el.textContent.replace(/\s+/g, ' ').trim().replace(/[,;]+$/, ''))
      .filter(Boolean)
      .join(', '),
    // 🔴🔴 幽灵行要记的是**折前价**，不是它还在车里时的折后价。
    // 行级折扣（第二双 40% off）是按整车分配的：这一行一旦移出购物车，那笔折扣
    // 就不再落在它身上了。沿用折后价会让一个「已取消勾选、不参与任何优惠」的商品
    // 上面挂着划线价和折扣价，等于承诺一个当下并不存在的价格。
    // 有划线价就用划线价（= original_line_price），没有就用行总价本身。
    priceText: (
      priceEl?.querySelector('.insmia-line-compare')?.textContent ||
      priceEl?.querySelector('text-component')?.textContent ||
      priceEl?.textContent ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim(),
    /* 🔴 幽灵行的排版必须和真实行**一模一样**（用户 2026-09-21：「取消全选后会变」）。
       规格块和数量块直接把真实行的那两格 HTML 存下来复用 —— 图标、下拉、按钮
       全是主题原样，比手搓保真，主题改版也不会两边走样。 */
    variantsHtml: row.querySelector('.cart-items__variants-wrapper')?.outerHTML || '',
    qtyHtml: row.querySelector('.cart-items__quantity')?.outerHTML || '',
    /* 数量在幽灵态下还能改，价格要跟着重算，所以记单价（折前，分） */
    unitCents: (function () {
      const quantity = Math.max(1, Number(row.dataset.quantity || '1') || 1);
      const text =
        priceEl?.querySelector('.insmia-line-compare')?.textContent ||
        priceEl?.querySelector('text-component')?.textContent ||
        priceEl?.textContent ||
        '';
      return Math.round(parseMoney(text) / quantity);
    })(),
    /* 单价格子（数量>1 且没合并时显示「$X each」）和真实的降价提示：幽灵行要照样占住这几行，
       行高才和勾选时一样（2026-09-28 用户：勾选前后排版都要动一下）。单价记折前的
       （data-insmia-unit-original，模板写的），这行不在车里就没有折扣。 */
    unitText: row.querySelector('.insmia-unit-price')?.dataset.insmiaUnitOriginal || '',
    dropText: (row.querySelector('[data-insmia-price-drop]:not([hidden])')?.textContent || '').trim(),
    savedAt: Date.now(),
  };
}

/** 幽灵行是用字符串拼 HTML 的，所有插值都过这道转义 —— 标题/规格里出现引号或尖括号时不会拼坏结构 */
function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c]);
}

function ghostRow(snap) {
  const tr = document.createElement('tr');
  tr.className = `cart-items__table-row cart-items__table-row--full-width-variants ${GHOST_CLASS}`;
  tr.dataset.variantId = String(snap.variantId);
  tr.dataset.quantity = String(snap.quantity || 1);
  tr.setAttribute('role', 'row');

  /* 规格块：原样复用真实行那一格（含「换尺码/颜色」按钮和它的隐藏 quick-add 壳）。
     只把隐藏的原生 <select> 摘掉 —— 它的 change 会走换变体链路，而这行不在购物车里。 */
  let variantsHtml = snap.variantsHtml || '';
  if (variantsHtml) {
    const holder = document.createElement('div');
    holder.innerHTML = variantsHtml;
    holder.querySelectorAll('.insmia-cart-variant-switch').forEach((n) => n.remove());
    /* 🔴 折扣标签在未选中的行上不成立：行级折扣是按整车分配的，这行既然不在车里
       就拿不到这笔折扣，上面价格已经写的是折前价，标签留着会自相矛盾。 */
    holder.querySelectorAll('.cart-items__discount').forEach((node) => {
      const list = node.closest('ul');
      (list || node).remove();
    });
    holder.querySelectorAll('[data-insmia-price-drop], .insmia-cart-rowerror').forEach((n) => n.remove());
    variantsHtml = holder.innerHTML;
  } else {
    variantsHtml = `<p class="insmia-cart-ghost__meta">${esc(snap.variantText)}</p>`;
  }

  /* 数量块同理原样复用。
     🔴 但必须先在字符串层把主题的绑定拆掉：
        · <cart-quantity-selector-component> 换成普通 div，自定义元素就不会升级它
        · on:click / on:blur / on:focus 全摘掉，主题的 document 级委托就分发不到
     否则它会拿一个根本不存在的行号去打 /cart/change.js。 */
  const qtyHtml = (snap.qtyHtml || '')
    .replace(/<cart-quantity-selector-component/g, '<div')
    .replace(/<\/cart-quantity-selector-component>/g, '</div>')
    .replace(/\son:(click|blur|focus)="[^"]*"/g, '');

  tr.innerHTML = `
    <td class="cart-items__select" role="cell">
      <label class="insmia-cart-check">
        <input type="checkbox" data-insmia-select data-variant-id="${esc(snap.variantId)}">
        <span class="visually-hidden">Select ${esc(snap.title)}</span>
      </label>
    </td>
    <td class="cart-items__media" role="cell">
      ${snap.image ? `<a href="${esc(snap.url)}" class="cart-items__media-container"><img src="${esc(snap.image)}" alt="" class="cart-items__media-image border-style" loading="lazy"></a>` : ''}
    </td>
    <td class="cart-items__details cart-primary-typography" role="cell">
      <div class="cart-items__product-info"><p><a href="${esc(snap.url)}" class="cart-items__title">${esc(snap.title)}</a></p></div>
      <div class="cart-items__variants-wrapper">${variantsHtml}</div>
      <div class="cart-items__unit-price-wrapper">${
        snap.unitText
          ? `<div class="insmia-unit-price"><span>${esc(snap.unitText)}</span><span class="insmia-unit-price__suffix">each</span></div>`
          : ''
      }${snap.dropText ? `<p class="insmia-price-drop">${esc(snap.dropText)}</p>` : ''}</div>
    </td>
    ${qtyHtml || '<td class="cart-items__quantity" role="cell"></td>'}
    <td class="cart-items__price cart-secondary-typography" role="cell"><span>${esc(
      formatMoney((snap.unitCents || 0) * (snap.quantity || 1), snap.priceText)
    )}</span></td>
  `;

  // 数量输入框接到快照上
  const input = tr.querySelector('.cart-items__quantity input');
  if (input) {
    input.value = String(snap.quantity || 1);
    input.removeAttribute('name');
    input.setAttribute('data-insmia-ghost-qty', '');
  }
  tr.querySelectorAll('.cart-items__quantity .quantity-minus, .cart-items__quantity .quantity-plus').forEach((button) => {
    button.setAttribute('data-insmia-ghost-step', button.classList.contains('quantity-plus') ? '1' : '-1');
    button.disabled = false;
  });

  return tr;
}

/* ------------------------------------------------- 幽灵行上的换尺码/颜色 */
/**
 * 换变体的弹窗由 insmia-cart-line-tools.js 负责开；但幽灵行不在购物车里，
 * 没有行可换 —— 它改的是 localStorage 里的快照。两个脚本各管各的模块作用域，
 * 所以用一个自定义事件做桥：line-tools 发 insmia:ghost-variant，这里接。
 */
document.addEventListener('insmia:ghost-variant', async (event) => {
  const detail = event.detail || {};
  const oldVariantId = String(detail.oldVariantId || '');
  const newVariantId = String(detail.variantId || '');
  const handle = String(detail.handle || '');
  if (!oldVariantId || !newVariantId || oldVariantId === newVariantId) return;

  const saved = readSaved();
  const snap = saved.find((s) => String(s.variantId) === oldVariantId);
  if (!snap) return;

  try {
    const product = await (await fetch(`/products/${encodeURIComponent(handle)}.js`)).json();
    const variant = (product.variants || []).find((v) => String(v.id) === newVariantId);
    if (!variant) return;

    const optionNames = (product.options || []).map((o) => (typeof o === 'string' ? o : o?.name || ''));

    snap.variantId = newVariantId;
    snap.variantText = variant.options
      .map((value, index) => (optionNames[index] ? `${optionNames[index]}: ${value}` : value))
      .join(', ');
    snap.unitCents = variant.price;
    snap.priceText = formatMoney(variant.price * (snap.quantity || 1), snap.priceText);
    snap.url = `/products/${handle}?variant=${newVariantId}`;
    if (variant.featured_image && variant.featured_image.src) snap.image = variant.featured_image.src;

    // 规格文案要跟着变，存下来的那块 HTML 里的旧值得同步掉
    if (snap.variantsHtml) {
      const holder = document.createElement('div');
      holder.innerHTML = snap.variantsHtml;
      const dl = holder.querySelector('.cart-items__variants');
      if (dl) {
        dl.innerHTML = variant.options
          .map(
            (value, index) =>
              `<div class="cart-items__variant"><dt class="visually-hidden">${esc(
                optionNames[index] || ''
              )}:</dt><dd>${esc(value)}${index < variant.options.length - 1 ? ',&nbsp;' : ''}</dd></div>`
          )
          .join('');
      }
      const label = holder.querySelector('[data-insmia-variant-trigger] > span');
      if (label) label.textContent = variant.title;
      snap.variantsHtml = holder.innerHTML;
    }

    // 幽灵行换了变体，顺序表也得原地改名：不然勾回购物车时它是个「没见过的变体」，
    // 会被插到最前，位置就跳了（和真实行换变体是同一个坑）
    cartOrder.rename(oldVariantId, newVariantId);

    writeSaved(saved);
    paint(true);
  } catch {
    /* 拉不到产品数据就维持原样，别把快照改成半截状态 */
  }
});

/* ------------------------------------------------- 幽灵行上的数量加减 */
/**
 * 🔴 改的是 localStorage 里的快照，不是购物车 —— 这一行当下根本不在购物车里。
 * 减到 0 等于真的丢弃它（和真实行上那个垃圾桶一致）。
 * 捕获阶段 + stopPropagation：别让主题的数量委托看见这次点击。
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('[data-insmia-ghost-step]') : null;
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const row = button.closest(`.${GHOST_CLASS}`);
    const variantId = String(row?.dataset.variantId || '');
    const step = Number(button.dataset.insmiaGhostStep) || 0;
    if (!variantId || !step) return;

    const saved = readSaved();
    const snap = saved.find((s) => String(s.variantId) === variantId);
    if (!snap) return;

    const next = Math.max(0, (Number(snap.quantity) || 1) + step);
    if (next === 0) {
      writeSaved(saved.filter((s) => String(s.variantId) !== variantId));
    } else {
      snap.quantity = next;
      writeSaved(saved);
    }
    paint(true);
  },
  true
);

/* --------------------------------------------------- 结算区快照（空态复用） */
/**
 * 🔴 全部取消勾选后 Shopify 的车确实空了，主题会整套换成「空车版面」——
 * 居中大标题、没有结算区、商品行也不是原来那套。用户 2026-09-20 的要求是
 * **「取消全选后还是那个勾选后的样式，不要动」**。
 *
 * 做法：趁购物车还非空的时候把整块结算区的 DOM 存进 sessionStorage，
 * 空态时原样复用（比手搓一份保真，主题改版也不会两边走样），
 * 只把「此刻不成立」的部分改掉：金额归零、折扣行删掉、结算按钮禁用、
 * 分期挂件删掉（它会自己去问 Shopify，复制出来的那个没有上下文）。
 */
const SUMMARY_KEY = 'insmia:cart:summary-shell:v1'; // 旧键（sessionStorage），只读兼容
/* 🔴 结算区快照必须存 localStorage，而且抽屉、购物车页分开存。
   ① 以前存 sessionStorage：按标签页隔离 ⇒ 全不选后在新标签页、或者关掉浏览器再回来，
      复原不出结算区（没有 Select all、没有结算按钮），顾客只能一件件重新勾（2026-09-26 实测）。
   ② 以前只有一个键：购物车页拍的快照外层是 .cart-page__summary，拿去抽屉复原就套不上
      抽屉悬浮结算条的样式（反之亦然）。 */
const SUMMARY_KEYS = { drawer: 'insmia:cart:summary-shell:v2:drawer', page: 'insmia:cart:summary-shell:v2:page' };
const SUMMARY_WRAPPERS = { drawer: 'cart-drawer__summary', page: 'cart-page__summary' };

const lastSummaryHtml = { drawer: '', page: '' };

/* 🔴 快照里要带上分期付款行 / Details 明细块的实际高度：全不选时它们不成立，幽灵结算区里换成同高的隐形占位，
   不然购物车页右栏矮 75px、CHECK OUT 往上跳 105px（2026-09-28 实测）。
   量高度要等这一帧画完：paint() 在购物车变动的那个任务里跑，这时量会逼浏览器当场多做一次整页样式 + 布局。 */
/** 这一页刚量到的分期行 / 明细块高度（按抽屉、购物车页分开）。和 flipCapture 同一时刻量：布局是干净的，不额外花钱。 */
const summaryHeights = { drawer: null, page: null };
function captureSummaryHeights() {
  for (const context of ['drawer', 'page']) {
    const el = document.querySelector('.' + SUMMARY_WRAPPERS[context]);
    if (!el || !el.querySelector('#checkout')) continue;
    const measure = (selector) => Math.round((el.querySelector(selector)?.getBoundingClientRect().height || 0) * 100) / 100; // 不取整：取整会差 0.x px、一路累积成 1px
    summaryHeights[context] = { installments: measure('.cart-totals__installments'), breakdown: measure('.insmia-price-breakdown') };
  }
}

let summaryShellQueued = false;
function rememberSummaryShell() {
  if (summaryShellQueued) return;
  summaryShellQueued = true;
  requestAnimationFrame(() =>
    setTimeout(() => {
      summaryShellQueued = false;
      saveSummaryShell();
    }, 0)
  );
}

function saveSummaryShell() {
  for (const context of ['drawer', 'page']) {
    const el = document.querySelector('.' + SUMMARY_WRAPPERS[context]);
    // 必须确认它是「有商品」时的那一份（带结算按钮），别把空态自己存进去
    if (!el || !el.querySelector('#checkout')) continue;

    const copy = el.cloneNode(true);
    const live = el.querySelectorAll('.cart-totals__installments, .insmia-price-breakdown');
    copy.querySelectorAll('.cart-totals__installments, .insmia-price-breakdown').forEach((node, index) => {
      const height = live[index] ? Math.round(live[index].getBoundingClientRect().height * 100) / 100 : 0;
      if (height) node.setAttribute('data-insmia-h', String(height));
    });

    // 🔴 paint() 每次购物车变动都会跑，outerHTML 序列化 + 写存储不算便宜，内容没变就别做
    const html = copy.outerHTML;
    if (html === lastSummaryHtml[context]) continue;
    lastSummaryHtml[context] = html;

    try {
      localStorage.setItem(SUMMARY_KEYS[context], html);
    } catch {
      /* 存不下就降级：空态没有结算区，但不影响购物车本身 */
    }
  }
}

/** 把金额串里的数字换成 0，沿用店铺自己的货币格式（$1,234.56 USD → $0.00 USD） */
function zeroMoney(text) {
  return String(text || '').replace(/\d[\d.,]*/, '0.00');
}

function buildGhostSummary(context = 'drawer') {
  const other = context === 'drawer' ? 'page' : 'drawer';
  let html = '';
  try {
    // 优先本场景的快照；没有就借另一个场景的；再没有才读旧的 sessionStorage 键
    html =
      localStorage.getItem(SUMMARY_KEYS[context]) ||
      localStorage.getItem(SUMMARY_KEYS[other]) ||
      sessionStorage.getItem(SUMMARY_KEY) ||
      '';
  } catch {
    html = '';
  }
  if (!html) return null;

  const holder = document.createElement('div');
  holder.innerHTML = html;
  const el = holder.firstElementChild;
  if (!el) return null;

  // 借来的快照外层类名换成本场景的，样式（抽屉悬浮结算条 / 购物车页右栏）才对得上
  el.classList.remove(SUMMARY_WRAPPERS[other]);
  el.classList.add(SUMMARY_WRAPPERS[context]);

  el.classList.add('insmia-ghost-summary');
  /* 🔴 自己的 id：快照是真结算区的 outerHTML，购物车页那份带着 id="insmia-cart-page-summary"（patch61），
     原样用会和真结算区重号；而且没有 key 的话，主题 morph 会按位置把它和推荐位配成一对、把推荐盒重建掉
     （全不选后推荐区消失 0.7 秒，2026-09-27 实测）。 */
  el.id = `insmia-ghost-summary-${context}`;
  // 一件都没选时这些都不成立
  /* 分期付款行、Details 明细在一件都没选时不成立（分期组件会拿快照里的旧金额自己去问 Shopify），
     但位置要占住：换成同高的隐形占位（购物车页右栏不跳）。高度优先用这一页刚量到的（postCart 落地前量的，
     分期组件是异步渲染的，存快照时它可能还是 0 高），其次用快照里记的；都没有就照旧删掉。 */
  const fresh = summaryHeights[context] || {};
  el.querySelectorAll('.cart-totals__installments, .insmia-price-breakdown').forEach((n) => {
    const kind = n.classList.contains('insmia-price-breakdown') ? 'breakdown' : 'installments';
    const height = fresh[kind] || Number(n.getAttribute('data-insmia-h') || 0);
    if (!height) {
      n.remove();
      return;
    }
    const holder = document.createElement('div');
    holder.className = n.className;
    holder.setAttribute('aria-hidden', 'true');
    holder.style.height = `${height}px`;
    holder.style.visibility = 'hidden';
    n.replaceWith(holder);
  });
  /* 「You save」那一行不删：改成隐形占位（和模板里没有优惠时一样），合计块高度才不随勾选变（2026-09-28） */
  el.querySelectorAll('.insmia-total-sub').forEach((n) => {
    n.classList.add('insmia-total-sub--empty');
    n.setAttribute('aria-hidden', 'true');
    n.innerHTML = '<s class="insmia-total-compare">&nbsp;</s>';
  });

  const totalEl = el.querySelector('.cart-totals__total-value');
  if (totalEl) totalEl.textContent = zeroMoney(totalEl.textContent);

  const checkout = el.querySelector('#checkout');
  if (checkout instanceof HTMLButtonElement) {
    // 🔴 id 必须摘掉：真实结算按钮回来时会和它撞号，syncControls 只会命中其中一个
    checkout.removeAttribute('id');
    checkout.removeAttribute('form');
    checkout.disabled = true;
    const label = checkout.querySelector('.button-text');
    if (label) label.textContent = label.textContent.replace(/\(\d+\)/, '(0)');
  }

  const selectAll = el.querySelector('[data-insmia-select-all]');
  if (selectAll instanceof HTMLInputElement) {
    selectAll.checked = false;
    selectAll.indeterminate = false;
  }

  return el;
}

/* ------------------------------------------------- 行顺序（换变体后不跳位） */
/**
 * 🔴 换变体 = 删旧行 + 加新行，而 Shopify 的 `/cart/add.js` 是**往前插**的。
 * 实测 2026-09-22：依次加 A、B、C，`/cart.js` 回来的是 [C, B, A]；
 * 换掉中间那行的尺码，那一行就跑到第一位 —— 不是主题在重排，是 Shopify 的顺序。
 * 用户要求：同一个产品换尺码或者颜色，切换后位置不要动。
 *
 * 所以自己记一份展示顺序，**按 variant_id 存，一行一个条目**：
 *   · 车里没见过的变体整块插到最前 —— 和 Shopify 一致，刚加购的仍然在最上面
 *   · 换变体时由 insmia-cart-line-tools.js 调 rename()，**原地**把旧 id 换成新 id，
 *     那一行的位置就守住了；同一个产品挂着两个尺码时也能各守各的位置
 *     （这就是不能按 product_id 存的原因 —— 两个尺码同一个 product_id，分不开）
 *   · 已经不在车里的 id 自动淘汰
 * 拿不到 localStorage（隐私模式）就退回 Shopify 的顺序，功能不受影响。
 */
const cartOrder = (window.insmiaCartOrder = {
  KEY: 'insmia:cart:order:v1',

  read() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.KEY) || '[]');
      return Array.isArray(raw) ? raw.map(String) : [];
    } catch {
      return [];
    }
  },

  write(list) {
    try {
      localStorage.setItem(this.KEY, JSON.stringify(list));
    } catch {
      /* 存不下就退回 Shopify 的顺序 */
    }
  },

  /**
   * 已退役的变体 id → 换成了谁。只在内存里，不能落盘（刷新后就该忘掉）。
   *
   * 🔴 为什么需要它：服务端换完变体到 DOM 重渲染之间有几百毫秒，这期间行上挂的
   * 还是**旧**变体 id。没有这张映射的话 enforceRowOrder 会把旧 id 当成
   * 「表里没见过 ⇒ 新加购的」插到最前，刚才那次原地替换就白做了。
   * 实测日志（2026-09-22）：rename 写入 [C,B',A]，紧接着被改成 [B,C,A]，
   * morph 之后又变成 [B',C,A] —— 换完的那行照样跳到第一位。
   */
  retired: new Map(),

  /** 换变体：原地替换，位置不动。旧 id 不在表里就当新行插到最前。 */
  rename(oldId, newId) {
    if (!oldId || !newId || String(oldId) === String(newId)) return;
    const from = String(oldId);
    const to = String(newId);

    const list = this.read();
    const at = list.indexOf(from);
    if (at === -1) list.unshift(to);
    else list[at] = to;
    this.write(list);

    this.retired.set(from, to);
    // 兜底：morph 没来（请求失败、用户跳走）也别让映射永久留着，
    // 否则真的重新加购这个变体时会被误认成换变体的残影。
    window.setTimeout(() => {
      if (this.retired.get(from) === to) this.retired.delete(from);
    }, 15000);
  },

  /** 行上还是旧 id 时，按它将要变成的那个 id 去查位置 */
  resolve(id) {
    return this.retired.get(String(id)) || String(id);
  },
});

/** 顺序表最多记这么多个变体，超了就清历史残留。够放几十行的购物车，不会无限长。 */
const ORDER_LIMIT = 50;

function enforceRowOrder() {
  const rows = [...document.querySelectorAll('.cart-items__table-row[data-key][data-variant-id]')];
  if (rows.length === 0) return;

  const raw = rows.map((row) => String(row.dataset.variantId));
  // 新变体已经出现在 DOM 里 ⇒ morph 落地了，退役映射用完就扔，别留着误伤后续加购
  for (const [oldId, newId] of cartOrder.retired) {
    if (raw.includes(newId)) cartOrder.retired.delete(oldId);
  }

  const present = raw.map((id) => cartOrder.resolve(id));
  const known = cartOrder.read();
  /* 🔴 没见过的变体按**当前次序整块**插到最前，别写成逐个 unshift ——
     那样首次加载会把整车顺序倒过来（[C,B,A] 变 [A,B,C]）。 */
  /* 🔴 不在版面上的 id **不要立刻淘汰**。取消勾选的商品是真的从购物车里删掉的
     （未选中态就是这么做的），而且有一瞬间两边都查不到它：购物车里已经没了、
     未选中快照还没写完 —— 实测取消勾选后第一次 paint 的 saved 还是空的。
     那一下把槽位丢掉，勾回来时 /cart/add.js 又把它插到最前，位置照样跳。
     留着不花钱（order.indexOf 查不到的 id 只是个空洞），只在超出上限时
     从末尾清掉那些既不在购物车、也没被幽灵行占着的历史残留。 */
  const fresh = present.filter((id) => !known.includes(id));
  const order = [...new Set([...fresh, ...known])];
  if (order.length > ORDER_LIMIT) {
    const keep = new Set([...present, ...readSaved().map((snap) => String(snap.variantId))]);
    for (let i = order.length - 1; i >= 0 && order.length > ORDER_LIMIT; i -= 1) {
      if (!keep.has(order[i])) order.splice(i, 1);
    }
  }
  cartOrder.write(order);

  document.querySelectorAll('.cart-items__table tbody').forEach((tbody) => {
    const live = [...tbody.querySelectorAll('.cart-items__table-row[data-key][data-variant-id]')];
    if (live.length < 2) return;

    const slot = (row) => order.indexOf(cartOrder.resolve(row.dataset.variantId));
    const sorted = [...live].sort((a, b) => slot(a) - slot(b));
    if (sorted.every((row, index) => row === live[index])) return;

    /* 🔴 只在「真实行原本占的那几个位置」之间重排，不能用 appendChild 一路往后搬 ——
       混合状态下 tbody 里还夹着幽灵行，往后搬会把真实行全挪到幽灵行后面，
       而这次 paint 可能因为签名没变就早退了、轮不到重新摆幽灵行。
       先在每行前面插一个注释占位，再把排好的行替换进去：位置集合不变，幽灵行不动。 */
    const marks = live.map((row) => tbody.insertBefore(document.createComment('i'), row));
    try {
      sorted.forEach((row, index) => tbody.replaceChild(row, marks[index]));
    } finally {
      // 中途出错也不能把占位注释留在 tbody 里：幽灵行是按 children 下标插回去的，
      // 多出来的节点会让它们插错位置
      for (const mark of marks) mark.remove();
    }
  });
}

/* ------------------------------------------------- 全不选时的推荐位挂载点 */
/**
 * 全不选时购物车在服务器上是空的，抽屉走的是空车模板，里面**没有推荐位的挂载点** ⇒
 * 推荐位整块消失、商品下面一大片空白（2026-09-26 用户 Chrome 实测）。
 * 按正常模板的位置（.cart-drawer__items 里、紧跟商品列表之后）补一个同样结构的挂载点，
 * 再通知 insmia-cart-recs.js 去填 —— 它会把之前那批推荐卡原样挂回来。
 * 只管抽屉；购物车页的推荐位挂在 blocks/_cart-products.liquid 里，另说。
 */
function ensureGhostRecsBox(wrap) {
  const items = wrap.closest('.cart-drawer__items');
  if (!items || items.querySelector('[data-insmia-recs]')) return;
  const box = document.createElement('div');
  box.id = 'insmia-recs-drawer';
  box.className = 'insmia-drawer-recs';
  box.setAttribute('data-insmia-recs', '');
  // 和模板里那个挂载点一样：购物车变动时 morph 不碰推荐格子（卡片不被拔下再插回、色卡不重收）
  box.setAttribute('data-skip-node-update', '');
  box.hidden = true;
  box.innerHTML =
    '<p class="insmia-drawer-recs__title">You may also like</p>' +
    '<div class="insmia-drawer-recs__grid" data-insmia-recs-grid data-skip-subtree-update></div>';
  items.appendChild(box);
  document.dispatchEvent(new CustomEvent('insmia:recs-refill'));
}

/* ------------------------------------------------- 幽灵表外层照抄真实表 */
/* 🔴 全不选时的幽灵表是 JS 拼的，外层以前写死 'cart-items spacing-style cart-items--dividers'。
   但抽屉的真实表没有 cart-items--dividers（那是区块设置里的「分隔线」开关，抽屉和购物车页各自一份），
   多出来的类给每行加了上下 14px 内边距和上边框 ⇒ 全不选时整列下移 15px、每行高 7px（2026-09-28 实测）。
   也少了 form.cart-form 那一层 —— 主题拿「抽屉里有没有 .cart-form」判断空车，一整套居中 / 空车样式会误伤幽灵态。
   ⇒ 有真实表时记下外层的 class / style（抽屉、购物车页分开存 localStorage：刷新后直接进全不选态也有），
   幽灵表照抄，外面再套一层 div.cart-form（不用 form：幽灵行里有数量输入框，回车会提交）。 */
const HOST_KEYS = { drawer: 'insmia:cart:items-host:v1:drawer', page: 'insmia:cart:items-host:v1:page' };
const lastHost = { drawer: '', page: '' };

function contextOf(el) {
  return el.closest('.cart-drawer') ? 'drawer' : 'page';
}

function rememberItemsHost() {
  document.querySelectorAll('.cart-form > .cart-items:not(.insmia-cart-ghost-host)').forEach((host) => {
    const context = contextOf(host);
    const value = JSON.stringify({ cls: host.className, style: host.getAttribute('style') || '' });
    if (value === lastHost[context]) return;
    lastHost[context] = value;
    try {
      localStorage.setItem(HOST_KEYS[context], value);
    } catch {
      /* 存不下就用默认外层，只是间距可能差一点 */
    }
  });
}

function ghostHostFor(wrap) {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(HOST_KEYS[contextOf(wrap)]) || 'null');
  } catch {
    saved = null;
  }
  const form = document.createElement('div');
  form.className = 'cart-form insmia-cart-ghost-form';
  const host = document.createElement('div');
  host.className = `${saved?.cls || 'cart-items spacing-style'} insmia-cart-ghost-host`;
  if (saved?.style) host.setAttribute('style', saved.style);
  form.appendChild(host);
  return { form, host };
}

/* ------------------------------------------------------------------ 重绘 */
let painting = false;
let lastSignature = '';

function signature(saved) {
  const liveKeys = [...document.querySelectorAll('.cart-items__table-row[data-key]')].map((r) => r.dataset.key).join(',');
  const ghostIds = [...document.querySelectorAll(`.${GHOST_CLASS}`)].map((r) => r.dataset.variantId).join(',');
  return `${saved.map((s) => s.variantId).join(',')}|${liveKeys}|${ghostIds}`;
}

/**
 * 自愈：未选中列表里的商品如果已经在购物车里（顾客从商品页又加购了同款同码），它就是「选中」的了，
 * 列表里那条要去掉 —— 不然会同时显示一行正常的 + 一行幽灵行。
 * 🔴 两道保险防误删（误删 = 商品两头都没了）：
 *   ① 本标签页有勾选相关请求在跑（draining）时不动：全不选是先写快照、再清购物车，
 *      中间约一秒「快照已写、商品还在车里」，这时删掉快照、购物车随后又被清空，商品就丢了；
 *   ② 快照写入不足 10 秒的不动：兜住另一个标签页正在做同样的事。
 */
function reconcileSaved() {
  if (draining) return;
  const saved = readSaved();
  if (!saved.length) return;
  const live = new Set(
    [...document.querySelectorAll('.cart-items__table-row[data-key][data-variant-id]')].map((row) =>
      String(row.dataset.variantId)
    )
  );
  if (!live.size) return;
  const now = Date.now();
  const keep = saved.filter(
    (snap) => !(live.has(String(snap.variantId)) && now - (Number(snap.savedAt) || 0) > 10000)
  );
  if (keep.length !== saved.length) writeSaved(keep);
}

function paint(force = false) {
  if (!ENABLED || painting) return;
  reconcileSaved();
  const saved = readSaved();
  const sig = signature(saved);
  /* 🔴 行顺序必须在签名早退**之前**修好。签名只看「被取消勾选的那些商品」——
     一个都没取消时它恒为空、永远等于 lastSignature，放在早退后面等于永远不执行，
     而换变体跳位恰恰发生在全选状态下。
     自己搬的 DOM 不要再触发自己的 observer。 */
  suspendObserver();
  try {
    enforceRowOrder();
  } finally {
    resumeObserver();
  }

  // 🔴 签名早退：morph 会引发一连串 DOM 变动，没有这道闸会空转很多次
  if (!force && sig === lastSignature) {
    // 早退也要按意图摆回勾选框：主题 morph 刚把排队中的那几件画回了服务器上的旧状态
    applyIntents();
    syncControls(saved);
    return;
  }

  painting = true;
  suspendObserver();
  try {
    // 真实行的顺序上面已经排好；幽灵行按记录的 index 插回去，两者相对位置才稳定
    // 按记录的原始位置插回，位置越靠前的先插，保证相对顺序稳定
    /* 正在被取消勾选、但还在购物车里的（全不选先写快照再清车，中间约一秒两边都有）不画幽灵行，
       不然同一件会同时出现一行真实的 + 一行幽灵的 */
    const liveNow = new Set(liveRows().map((row) => String(row.dataset.variantId)));
    const ordered = [...saved]
      .filter((snap) => !(liveNow.has(String(snap.variantId)) && intents.get(String(snap.variantId))?.want === 'out'))
      .sort((a, b) => (a.index ?? 1e9) - (b.index ?? 1e9));
    document.querySelectorAll('.cart-items__table tbody').forEach((tbody) => {
      tbody.querySelectorAll(`.${GHOST_CLASS}`).forEach((el) => el.remove());
      for (const snap of ordered) {
        const at = Math.min(snap.index ?? tbody.children.length, tbody.children.length);
        tbody.insertBefore(ghostRow(snap), tbody.children[at] || null);
      }
    });

    const cartIsEmpty = !document.querySelector('.cart-items__table-row[data-key]');
    // 这个类挂在 <html> 上，CSS 靠它把主题的空车版面收起来、换回正常购物车那一套
    document.documentElement.classList.toggle('insmia-has-unselected', saved.length > 0 && cartIsEmpty);

    if (saved.length > 0 && cartIsEmpty) {
      // 🔴 目标是**版面一点都不变**：和有商品时同样的商品行、同样的底部结算区，
      // 区别只有「一个都没勾」。所以这里不再写「Nothing selected」大标题、
      // 不再另做一个 SELECT ALL 按钮 —— 恢复入口就是结算区里原本那个 Select all。
      // 抽屉的空车标题是 h2.cart-drawer__heading--empty，把 --empty 摘掉它就回到
      // 正常标题的样式；文案改成 Cart，不要写「空了」。
      // 🔴 直接把主题的「空车」类摘掉，比逐条去盖它那些居中/隐藏规则干净得多。
      // 不用担心还原：购物车由空变非空一定伴随 section 重渲染，类跟着服务端回来。
      document.querySelectorAll('.cart-page--empty').forEach((el) => el.classList.remove('cart-page--empty'));
      document.querySelectorAll('.cart-drawer--empty').forEach((el) => el.classList.remove('cart-drawer--empty'));

      // 标题两处都要：抽屉是 h2.cart-drawer__heading--empty，购物车页是 .cart-title 里的标题
      // 正常态标题是「Cart ⑥」带一个数量角标，未选中态只写「Cart」会少一块。
      // 🔴 每次 paint 都重算：幽灵行上还能改数量，角标要跟着走。
      const savedUnits = saved.reduce((total, item) => total + (Number(item.quantity) || 1), 0);
      document.querySelectorAll('.cart-drawer__heading--empty, [data-insmia-rewritten="1"], .cart-title h1, .cart-title h2').forEach((h) => {
        if (!h.matches('.cart-drawer__heading, .cart-title h1, .cart-title h2')) return;
        h.dataset.insmiaRewritten = '1';
        h.classList.remove('cart-drawer__heading--empty');
        h.textContent = 'Cart';

        const bubble = document.createElement('span');
        bubble.className = 'cart-bubble';
        bubble.setAttribute('data-maintain-ratio', '');
        bubble.innerHTML =
          '<span class="cart-bubble__background"></span>' +
          `<span class="cart-bubble__text">${esc(savedUnits)}</span>`;
        h.appendChild(bubble);
      });

      /* 🔴 抽屉标题要回到头部那一行（和 ✕ 同一行）。空车模板把标题放在头部**下面**的
         .cart-drawer__content 里，只改文字的话「Cart ⑤」会掉到 ✕ 下面一行，
         比勾选状态矮一截（2026-09-26 用户 Chrome 实测）。正常模板里标题就在
         .cart-drawer__header 里、排在 ✕ 前面 —— 挪过去结构就一样了。
         不用还原：购物车由空变非空时整块头部跟着服务端重渲染回来。 */
      document.querySelectorAll('.cart-drawer .cart-drawer__heading[data-insmia-rewritten="1"]').forEach((h) => {
        const header = h.closest('.cart-drawer__content')?.previousElementSibling;
        if (header?.classList.contains('cart-drawer__header') && h.parentElement !== header) header.prepend(h);
      });

      // 标题下面那一行：空车模板里预埋了（insmia-shipping-bar.liquid 的 ghost_note），显示出来占住位置
      document.querySelectorAll('[data-insmia-ghost-note]').forEach((note) => {
        note.hidden = false;
      });

      document.querySelectorAll('.cart-items__wrapper').forEach((wrap) => {
        if (!wrap.querySelector('.cart-items__table')) {
          /* 🔴🔴 必须套一层 .cart-items。行的网格里用了 `clamp(2.5rem, 15cqi, 7.5rem)`，
             而 container-name/container-type 是挂在 .cart-items 上的；
             不套容器的话 15cqi 没有查询容器、退回按视口算 ——
             实测 1180px 视口下图片列被顶到 7.5rem 上限(120px)，正常行只有 107px，
             窄抽屉里差得更离谱，标题被挤成一条细柱（用户 2026-09-21 的截图就是这个）。
             同时 @container cart-items 的那几条规则也全都匹配不上。 */
          const { form, host } = ghostHostFor(wrap);

          const holder = document.createElement('table');
          holder.className = 'cart-items__table insmia-cart-ghost-only';
          const tbody = document.createElement('tbody');
          for (const snap of ordered) tbody.appendChild(ghostRow(snap));
          holder.appendChild(tbody);

          host.appendChild(holder);
          wrap.prepend(form);
        }

        ensureGhostRecsBox(wrap);

        // 结算区：空态下主题根本不渲染它，用非空时存下的那一份原样补回来。
        // 🔴 购物车页是 grid 布局、结算区有自己的网格区域（.cart-page__summary），
        // 直接 append 到 .cart-page 上会落进未定义区域、位置全乱 —— 必须放进那个壳里，
        // 壳不存在就补一个。
        const page = wrap.closest('.cart-page');
        const scope = wrap.closest('.cart-drawer__content') || page;
        if (!scope || scope.querySelector('.insmia-ghost-summary')) return;

        const summary = buildGhostSummary(scope === page ? 'page' : 'drawer');
        if (!summary) return;

        if (page) {
          const host = page.querySelector(':scope > .cart-page__summary');
          if (host) {
            host.appendChild(summary);
          } else {
            // 快照外层本身就是 .cart-page__summary（buildGhostSummary 按场景换好了类名），
            // 直接当壳插进去，不再套一层同名的壳。
            // 🔴 紧跟商品区插入，不能 append 到最后：推荐位是网格里的独立一块、排在结算区后面，
            // 追加到最后的话手机单栏时结算区会跑到 6 行推荐卡下面。
            const items = page.querySelector(':scope > .cart-page__items');
            if (items) items.after(summary);
            else page.appendChild(summary);
          }
        } else {
          scope.appendChild(summary);
        }
      });
    } else {
      // 🔴 只清标记，绝不把旧文案写回去：购物车由空变非空一定伴随 section 重渲染，
      // 标题以服务端为准；回写会把「Your cart is empty」盖到非空购物车上。
      document.querySelectorAll('[data-insmia-rewritten="1"]').forEach((h) => {
        delete h.dataset.insmiaRewritten;
      });
      document.querySelectorAll('.insmia-ghost-summary').forEach((n) => n.remove());
      rememberSummaryShell();
      rememberItemsHost();
    }

    applyIntents();
    syncControls(saved);
    lastSignature = signature(saved);
  } finally {
    painting = false;
    resumeObserver();
  }
}

/** 控件同步单独抽出来：乐观 UI 要在不重绘整个列表的情况下先更新它们。
 *  🔴 按「真实状态 + 还没落地的点击意图」算（2026-09-28），排队期间 Select all / N unselected 不来回跳。 */
function syncControls(saved = readSaved()) {
  const want = (v) => intents.get(v)?.want;
  const liveIds = new Set(liveRows().map((row) => String(row.dataset.variantId)));
  const unselected = new Set();
  saved.forEach((snap) => {
    const v = String(snap.variantId);
    if (want(v) !== 'in' && !(liveIds.has(v) && want(v) !== 'out')) unselected.add(v);
  });
  liveIds.forEach((v) => {
    if (want(v) === 'out') unselected.add(v);
  });
  let selected = 0;
  liveIds.forEach((v) => {
    if (want(v) !== 'out') selected += 1;
  });
  saved.forEach((snap) => {
    const v = String(snap.variantId);
    if (want(v) === 'in' && !liveIds.has(v)) selected += 1;
  });
  const anySaved = unselected.size > 0;
  document.querySelectorAll('[data-insmia-select-all]').forEach((el) => {
    if (!(el instanceof HTMLInputElement)) return;
    el.checked = !anySaved;
    // 🔴 部分选中要用 indeterminate。只把勾去掉的话，车里明明还选着两件，
    // 「Select all」却是个空方框，看上去像一件都没选。
    el.indeterminate = anySaved && selected > 0;
  });
  document.querySelectorAll('[data-insmia-saved-count]').forEach((el) => {
    el.textContent = anySaved ? `${unselected.size} unselected` : '';
  });
  // 结算按钮看真实购物车：还没加回去的不能结算
  const liveCount = document.querySelectorAll('.cart-items__table-row[data-key]:not(.insmia-pending-out)').length;
  document.querySelectorAll('#checkout').forEach((btn) => {
    if (btn instanceof HTMLButtonElement) btn.disabled = liveCount === 0;
  });
}

/* ------------------------------------------------------------------ 观察器 */
// 🔴 只观察购物车容器，不要在 document.body 上挂 subtree —— 首版那样做直接导致 1.1s 卡顿。
let observer = null;
let suspended = 0;
let observeTimer = null;

function suspendObserver() { suspended++; }
function resumeObserver() { suspended = Math.max(0, suspended - 1); }

function attachObserver() {
  observer?.disconnect();
  observer = new MutationObserver(() => {
    if (painting || suspended > 0) return;

    /* 🔴 行顺序必须**当帧**修好，不能跟着下面那个 80ms 防抖走。
       morph 落地的瞬间 DOM 是 Shopify 的顺序（换完变体的那行在最前），
       晚 80ms 再搬 ≈ 5 帧，用户看到的就是「先跳到顶部、再弹回原位」
       （2026-09-22 用户反馈；位置结果是对的，过程露馅了）。
       MutationObserver 回调是微任务，在浏览器**绘制之前**执行 ——
       在这里同步搬完，错误顺序一帧都不会露出来。
       enforceRowOrder 在顺序没变时直接早退，比整轮重绘便宜得多，每次都跑没问题。 */
    suspendObserver();
    try {
      enforceRowOrder();
    } finally {
      resumeObserver();
    }
    // 主题自己的 morph（cart:update 里）也会把排队中的勾选画回旧状态：同一个微任务里按意图摆回，一帧都不露
    applyIntents();
    syncControls();

    clearTimeout(observeTimer);
    observeTimer = setTimeout(() => paint(), 80);
  });
  const targets = document.querySelectorAll('.cart-items__wrapper');
  targets.forEach((t) => observer.observe(t, { childList: true, subtree: true }));
}

/* ------------------------------------------------ 勾选意图（连点不反弹） */
/* 🔴🔴 2026-09-28 用户：连续点几件时勾选框会「反弹」—— 勾上的过一两秒自己变回没勾（或反过来），再过一两秒才对。
   逐帧复现：每件都排队等服务器，前一件落地时整块重画（服务端 HTML + 按快照重建幽灵行），
   还在排队的那几件被画回「服务器上的旧状态」，轮到它们自己落地才变回来。
   ⇒ 点下去就记下这件「要变成什么」（意图）。任何重画之后都按意图重新摆勾选框；
   任务执行时只认最新意图（被后来的点击覆盖的直接跳过），按此刻的真实状态决定发不发请求；
   这件最后一次点击对应的任务结束，才清掉意图。 */
const intents = new Map(); // variantId → { want: 'in' | 'out', seq }
let intentSeq = 0;

function setIntent(variantId, want) {
  const seq = ++intentSeq;
  intents.set(String(variantId), { want, seq });
  return seq;
}

/** 这一次点击还是不是这件的「最后一次」 */
function intentCurrent(variantId, seq) {
  return intents.get(String(variantId))?.seq === seq;
}

function clearIntent(variantId, seq) {
  if (intentCurrent(variantId, seq)) intents.delete(String(variantId));
}

const rowSelector = (variantId) => `.cart-items__table-row[data-variant-id="${CSS.escape(String(variantId))}"]`;
function rowsOf(variantId) {
  return [...document.querySelectorAll(rowSelector(variantId))];
}
function liveRowsOf(variantId) {
  return rowsOf(variantId).filter((row) => row.hasAttribute('data-key'));
}
function liveRows() {
  return [...document.querySelectorAll('.cart-items__table-row[data-key][data-variant-id]')];
}

/** 按意图摆勾选框和置灰（重画之后调用；没有意图的行不碰） */
function applyIntents() {
  if (!intents.size) return;
  for (const [variantId, { want }] of intents) {
    for (const row of rowsOf(variantId)) {
      const ghost = row.classList.contains(GHOST_CLASS);
      const box = row.querySelector('[data-insmia-select]');
      if (want === 'out' && !ghost) {
        row.classList.add('insmia-pending-out');
        if (box) box.checked = false;
      } else if (want === 'in' && ghost) {
        row.classList.add('insmia-pending-in');
        if (box) box.checked = true;
      }
    }
  }
}

/** 意图清掉之后，按真实状态摆回这件的勾选框（请求失败、没发生 morph 时要靠它复原） */
function syncRowDisplay(variantId) {
  if (intents.has(String(variantId))) return;
  for (const row of rowsOf(variantId)) {
    const ghost = row.classList.contains(GHOST_CLASS);
    row.classList.remove('insmia-pending-in', 'insmia-pending-out');
    const box = row.querySelector('[data-insmia-select]');
    if (box) box.checked = !ghost;
  }
}

/* ------------------------------------------------------------------ 交互 */
/** 某个变体当前的幽灵行（抽屉和购物车页可能各有一行） */
function ghostRowsOf(variantId) {
  return [...document.querySelectorAll(`.${GHOST_CLASS}[data-variant-id="${CSS.escape(String(variantId))}"]`)];
}

function rowError(row, message) {
  if (!row) return;
  let el = row.querySelector('.insmia-cart-rowerror');
  if (!el) {
    el = document.createElement('div');
    el.className = 'insmia-cart-rowerror';
    row.appendChild(el);
  }
  el.textContent = message;
}

function onToggle(input) {
  const row = input.closest('tr');
  const variantId = String(input.dataset.variantId || '');
  if (!variantId) return;
  const want = input.checked ? 'in' : 'out';
  // 记住点的是哪一行（同一变体带不同自定义属性时会有好几行，行 id 是稳定的）
  const rowId = row?.id || '';
  const seq = setIntent(variantId, want);
  applyIntents();
  syncControls();

  enqueue(async () => {
    // 同一件后来又被点过：这一次作废，交给最后那次的任务（改主意的直接不发请求）
    if (!intentCurrent(variantId, seq)) return;
    let failure = '';
    try {
      if (want === 'out') {
        const target = (rowId && document.getElementById(rowId)?.hasAttribute('data-key') && document.getElementById(rowId)) ||
          liveRowsOf(variantId)[0];
        if (target) {
          /* 🔴 合并行代表同一变体被 BXGY 拆开的多个行项目，按一个 key 清零只会移掉其中一行；
             removeLines 按这一行带的**全部** key 清零，一次请求。快照在发请求前这一刻拍（位置、数量都是最新的）。 */
          const snap = snapshotRow(target);
          await removeLines([target]);
          const saved = readSaved();
          writeSaved([...saved.filter((s) => String(s.variantId) !== variantId), snap]);
        }
      } else {
        const snap = readSaved().find((s) => String(s.variantId) === variantId);
        if (snap) {
          const data = await addBack([{ id: Number(variantId), quantity: snap.quantity }]);
          if (!landed(data, variantId)) throw new Error('Sold out');
          // 等请求的这一两秒里幽灵行上的数量可能被改过：重新读一遍再删，别拿请求前那份覆盖回去
          writeSaved(readSaved().filter((s) => String(s.variantId) !== variantId));
        }
      }
    } catch (e) {
      failure =
        want === 'out'
          ? 'Could not update. Please try again.'
          : /sold out|unavailable|not enough/i.test(String(e.message))
            ? 'Sold out — no longer available.'
            : 'Could not add back. Please try again.';
    }
    clearIntent(variantId, seq);
    paint(true);
    syncRowDisplay(variantId);
    // 🔴 提示要写在 paint 之后：paint(true) 会把幽灵行整行重建，先写上去的提示跟着旧行一起丢了
    if (failure) (want === 'out' ? liveRowsOf(variantId) : ghostRowsOf(variantId)).forEach((r) => rowError(r, failure));
    flipPlay();
  });
}

/** 全选：把所有没选的加回来；还在排队「取消勾选」的也一并作废（以最后一次操作为准） */
function restoreAll() {
  const saved = readSaved();
  const pendingOut = liveRows()
    .map((row) => String(row.dataset.variantId))
    .filter((v) => intents.get(v)?.want === 'out');
  const ids = [...new Set([...saved.map((s) => String(s.variantId)), ...pendingOut])];
  if (!ids.length) return;
  /* 乐观：点下去当场每一行都勾上、「N unselected」去掉（用户 2026-09-27：「反应很慢」）。 */
  const seqs = new Map(ids.map((v) => [v, setIntent(v, 'in')]));
  applyIntents();
  syncControls();

  enqueue(async () => {
    const items = readSaved()
      .filter((s) => intentCurrent(String(s.variantId), seqs.get(String(s.variantId))))
      .map((s) => ({ id: Number(s.variantId), quantity: s.quantity }));
    let missing = [];
    let failed = false;
    if (items.length) {
      try {
        const data = await addBack(items);
        // 只删真的加回去了的；没加上的（售罄等）快照留着，让用户逐个试
        const back = new Set(items.filter((item) => landed(data, item.id)).map((item) => String(item.id)));
        missing = items.map((item) => String(item.id)).filter((id) => !back.has(id));
        writeSaved(readSaved().filter((s) => !back.has(String(s.variantId))));
      } catch {
        failed = true;
      }
    }
    for (const [v, seq] of seqs) clearIntent(v, seq);
    paint(true);
    ids.forEach(syncRowDisplay);
    // 没加回去的那几行要说一声，不然只是勾自己弹回去（提示写在 paint 重建之后）
    missing.forEach((id) => ghostRowsOf(id).forEach((ghost) => rowError(ghost, 'Sold out — no longer available.')));
    if (failed) {
      const first = document.querySelector(`.${GHOST_CLASS}`);
      if (first) rowError(first, 'Could not add back. Please try again.');
    }
    flipPlay();
  });
}

/**
 * 全不选：把车里所有行一次性移出并存进 localStorage；还在排队「勾回」的也一并作废。
 *
 * 🔴 顺序不能反：**先把快照写进 localStorage 并读回来验证通过**，再动购物车。
 * 反过来的话，localStorage 写失败（无痕模式/配额满）等于把用户的车清空且无法恢复。
 * 网络那步失败也要把 saved 回滚，否则幽灵行和真实购物车会对不上。
 * 快照在任务真正执行时才拍：前面排队的操作落地后，行的位置、数量都可能变了。
 */
function unselectAll() {
  const pendingIn = readSaved()
    .map((s) => String(s.variantId))
    .filter((v) => intents.get(v)?.want === 'in');
  const ids = [...new Set([...liveRows().map((row) => String(row.dataset.variantId)), ...pendingIn])];
  if (!ids.length) return;
  // 乐观：点下去当场就把每一行的勾去掉（以前要等两次往返、1 秒多才变，用户 2026-09-27：「反应很慢」）
  const seqs = new Map(ids.map((v) => [v, setIntent(v, 'out')]));
  applyIntents();
  syncControls();

  enqueue(async () => {
    const rows = liveRows().filter((row) => {
      const v = String(row.dataset.variantId);
      return intentCurrent(v, seqs.get(v));
    });
    let failed = false;
    if (rows.length) {
      const snaps = [];
      for (const row of rows) {
        const snap = snapshotRow(row);
        if (snap.variantId && !snaps.some((n) => String(n.variantId) === String(snap.variantId))) snaps.push(snap);
      }
      const prior = readSaved();
      const next = [...prior.filter((s) => !snaps.some((n) => String(n.variantId) === String(s.variantId))), ...snaps];
      writeSaved(next);
      if (readSaved().length !== next.length) {
        failed = true;
      } else {
        try {
          // 🔴 不用 /cart/clear.js：clear 会顺手把车上的折扣码之类的状态一起抹掉，这里只想移除行项目。
          await removeLines(rows);
        } catch {
          writeSaved(prior);
          failed = true;
        }
      }
    }
    for (const [v, seq] of seqs) clearIntent(v, seq);
    paint(true);
    ids.forEach(syncRowDisplay);
    if (failed) {
      const first = liveRows()[0];
      if (first) rowError(first, 'Could not update. Please try again.');
    }
    flipPlay();
  });
}

document.addEventListener('change', (e) => {
  const t = e.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (t.hasAttribute('data-insmia-select-all')) {
    // 用户 2026-09-20 点名要反选。全不选 = 全部移进 localStorage，
    // 空车态会渲染「Nothing selected」+ 一键恢复按钮，所以是可逆的。
    if (t.checked) restoreAll();
    else unselectAll();
    return;
  }
  if (t.hasAttribute('data-insmia-select')) onToggle(t);
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  attachObserver();
  paint();
});
document.addEventListener('shopify:section:load', () => {
  attachObserver();
  paint(true);
});

function boot() {
  attachObserver();
  paint(true);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
